import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';

/* ── Types ─────────────────────────────────────────────── */

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  exiting?: boolean;
}

interface ToastContextType {
  toast: {
    success: (message: string, duration?: number) => void;
    error: (message: string, duration?: number) => void;
    warning: (message: string, duration?: number) => void;
    info: (message: string, duration?: number) => void;
  };
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const MAX_TOASTS = 5;
const DEFAULT_DURATION = 4000;

const TOAST_CONFIG: Record<ToastType, { icon: string; color: string }> = {
  success: { icon: 'check_circle', color: '#22c55e' },
  error: { icon: 'error', color: '#ef4444' },
  warning: { icon: 'warning', color: '#f59e0b' },
  info: { icon: 'info', color: '#3b82f6' },
};

/* ── Single Toast Item ─────────────────────────────────── */

const ToastItem: React.FC<{
  toast: Toast;
  onRemove: (id: string) => void;
}> = ({ toast: t, onRemove }) => {
  const config = TOAST_CONFIG[t.type];
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);
  const itemRef = useRef<HTMLDivElement>(null);

  // Auto-dismiss timer
  useEffect(() => {
    const timer = setTimeout(() => onRemove(t.id), t.duration);
    return () => clearTimeout(timer);
  }, [t.id, t.duration, onRemove]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    if (itemRef.current && diff > 0) {
      itemRef.current.style.transform = `translateX(${diff}px)`;
      itemRef.current.style.opacity = `${Math.max(0, 1 - diff / 200)}`;
    }
  };

  const handleTouchEnd = () => {
    const diff = touchCurrentX.current - touchStartX.current;
    if (diff > 80) {
      onRemove(t.id);
    } else if (itemRef.current) {
      itemRef.current.style.transform = '';
      itemRef.current.style.opacity = '';
    }
  };

  return (
    <div
      ref={itemRef}
      role="alert"
      aria-live="assertive"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`
        relative overflow-hidden rounded-xl border border-slate-200
        bg-white/95 backdrop-blur-sm shadow-lg
        transition-transform duration-200 ease-out
        ${t.exiting ? 'toast-exit' : 'toast-enter sm:toast-enter-desktop'}
      `}
    >
      {/* Left color stripe */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
        style={{ backgroundColor: config.color }}
      />

      {/* Content */}
      <div className="flex items-start gap-3 py-3 pl-4 pr-3">
        <span
          className="material-symbols-outlined text-xl mt-0.5 shrink-0"
          style={{ color: config.color }}
        >
          {config.icon}
        </span>
        <p className="text-sm text-slate-700 font-medium flex-1 leading-snug">
          {t.message}
        </p>
        <button
          onClick={() => onRemove(t.id)}
          className="text-slate-400 hover:text-slate-600 transition-colors shrink-0 mt-0.5"
          aria-label="Close"
        >
          <span className="material-symbols-outlined text-lg">close</span>
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-0.5 w-full bg-slate-100">
        <div
          className="h-full toast-progress"
          style={{
            backgroundColor: config.color,
            animationDuration: `${t.duration}ms`,
          }}
        />
      </div>
    </div>
  );
};

/* ── Toast Container (Portal) ──────────────────────────── */

const ToastContainer: React.FC<{
  toasts: Toast[];
  onRemove: (id: string) => void;
}> = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return ReactDOM.createPortal(
    <div
      aria-live="polite"
      className="
        fixed z-[9999] pointer-events-none
        top-0 left-0 right-0 p-2
        sm:top-4 sm:right-4 sm:left-auto sm:p-0 sm:w-96
      "
    >
      <div className="flex flex-col gap-2">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto">
            <ToastItem toast={t} onRemove={onRemove} />
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

/* ── Provider ──────────────────────────────────────────── */

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => {
      const target = prev.find((t) => t.id === id);
      if (!target || target.exiting) return prev;
      return prev.map((t) => (t.id === id ? { ...t, exiting: true } : t));
    });
    // Remove from DOM after exit animation
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 300);
  }, []);

  const addToast = useCallback((type: ToastType, message: string, duration = DEFAULT_DURATION) => {
    const id = crypto.randomUUID();
    setToasts((prev) => {
      const next = [...prev, { id, type, message, duration }];
      // Enforce max limit — remove oldest
      if (next.length > MAX_TOASTS) {
        return next.slice(next.length - MAX_TOASTS);
      }
      return next;
    });
  }, []);

  const toast = {
    success: useCallback((msg: string, dur?: number) => addToast('success', msg, dur), [addToast]),
    error: useCallback((msg: string, dur?: number) => addToast('error', msg, dur), [addToast]),
    warning: useCallback((msg: string, dur?: number) => addToast('warning', msg, dur), [addToast]),
    info: useCallback((msg: string, dur?: number) => addToast('info', msg, dur), [addToast]),
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

/* ── Hook ──────────────────────────────────────────────── */

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};
